import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import {
  BackLink,
  BranchSelect,
  Notice,
  Pill,
  ResultChip,
  SearchField,
  Segmented,
  serviceSummary,
} from "../../components/Fleet/FleetParts";
import FleetMap, { MapLegend } from "../../components/Fleet/FleetMap";
import {
  HISTORY_DAYS,
  daysAgo,
  formatAge,
  formatDateTime,
  formatDuration,
  formatMiles,
  gpsPlace,
  gpsState,
  relativeDay,
  vehicleModel,
} from "../../components/Fleet/fleetFormat";
import { vehicleOrigin } from "../../components/Fleet/VehicleDialogs";
import { fetchVehicles } from "./fleetApi";

// Every vehicle in Bistrack, merged with its live GPS Insight position and
// its pre-trip inspections from the mobile app. Filters live in the URL
// (?branch=&status=&q=&view=map, &removed=1 for the trucks a Fleet Manager
// removed — those are left out of everything else on the page).
// Names and branches are the edited ones where a truck was renamed or moved.

// GPS positions go stale fast; refresh while the page is open and visible.
const REFRESH_MS = 2 * 60 * 1000;

const STATUSES = {
  all: { label: "All vehicles", test: () => true },
  today: { label: "Inspected today", test: (v) => daysAgo(v.stats.lastAt) === 0 },
  notToday: {
    label: "Not inspected today",
    test: (v) => v.inBistrack && daysAgo(v.stats.lastAt) !== 0,
  },
  oos: { label: "Out of service", hint: "Marked, with a reason", test: (v) => !v.inService },
  flagged: { label: "Flagged", hint: `Last ${HISTORY_DAYS} days`, test: (v) => v.stats.flagged > 0 },
  missed: {
    label: "Drove, no inspection",
    hint: `Last ${HISTORY_DAYS} days`,
    test: (v) => v.stats.missed > 0,
  },
  running: { label: "Running now", hint: "Ignition on", test: (v) => v.gps?.ignition === "on" },
  noGps: { label: "No GPS", hint: "In Bistrack", test: (v) => v.inBistrack && !v.gps },
};
const STATUS_TONES = { today: "emerald", notToday: "amber", oos: "violet", flagged: "amber", missed: "rose", running: "sky", noGps: "default" };

export const odometerOf = (vehicle) => vehicle.gps?.odometer || vehicle.stats.lastOdometer || "";

const VehicleList = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const branch = params.get("branch") || "";
  const status = STATUSES[params.get("status")] ? params.get("status") : "all";
  const query = params.get("q") || "";
  const removedView = params.get("removed") === "1";
  // Removed trucks are a plain list — no map, no status tiles.
  const view = !removedView && params.get("view") === "map" ? "map" : "list";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== "list") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      setData(await fetchVehicles(user.id));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const removed = useMemo(() => data?.removed || [], [data]);
  const vehicles = useMemo(
    () => (removedView ? removed : data?.vehicles || []),
    [data, removed, removedView]
  );

  const branches = useMemo(
    () => [...new Set(vehicles.map((v) => v.branch).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [vehicles]
  );
  const selectedBranch = branches.find((b) => b.toLowerCase() === branch.toLowerCase()) || "";

  const inBranch = useMemo(
    () => vehicles.filter((v) => !branch || v.branch.toLowerCase() === branch.toLowerCase()),
    [vehicles, branch]
  );

  const counts = useMemo(
    () => Object.fromEntries(Object.entries(STATUSES).map(([key, s]) => [key, inBranch.filter(s.test).length])),
    [inBranch]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inBranch.filter(removedView ? () => true : STATUSES[status].test).filter(
      (v) =>
        !q ||
        [
          v.label,
          v.label.replace(/^#/, ""),
          // A renamed or moved truck is still found by its Bistrack values.
          v.renamed && v.bistrackLabel,
          v.renamed && v.bistrackLabel?.replace(/^#/, ""),
          v.moved && v.bistrackBranch,
          v.vin,
          v.branch,
          vehicleModel(v.info),
          v.gps?.driver,
          gpsPlace(v.gps),
          v.stats.lastInspector,
          v.service?.reasonLabel,
          v.service?.note,
        ]
          .filter(Boolean)
          .some((text) => text.toLowerCase().includes(q))
    );
  }, [inBranch, status, query, removedView]);

  const mapCounts = useMemo(() => {
    const tally = {};
    visible.forEach((v) => {
      if (v.gps?.lat === null || !v.gps) return;
      const key = gpsState(v.gps).key;
      tally[key] = (tally[key] || 0) + 1;
    });
    return tally;
  }, [visible]);

  return (
    <PageShell size="2xl">
      <BackLink to="/fleet">Fleet</BackLink>
      <PageHeader
        icon={LocalShippingRoundedIcon}
        tone="teal"
        eyebrow="Fleet"
        title="Vehicles"
        description={`Every truck in Bistrack with its live GPS position and ${HISTORY_DAYS} days of pre-trip inspections from the mobile app.`}
        actions={
          <>
            {data?.fetchedAt && (
              <span className="app-muted text-xs tabular-nums">
                Updated {new Date(data.fetchedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            <button type="button" className="app-btn app-btn-secondary" onClick={load} disabled={loading}>
              <RefreshRoundedIcon sx={{ fontSize: 18 }} /> {loading ? "Refreshing…" : "Refresh"}
            </button>
          </>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {data?.gpsError && <Notice>{data.gpsError} Inspections still show; positions will fill in once GPS Insight answers.</Notice>}

      {!data && !error && <LoadingState label="Loading vehicles and live GPS…" />}

      {data && (
        <>
          {removedView ? (
            <Notice tone="slate">
              Removed trucks are left out of every vehicle list, count, map and widget here and in the mobile app.
              Their history is kept — open one and click Restore to put it back.
            </Notice>
          ) : (
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
              {Object.entries(STATUSES).map(([key, s]) => (
                <StatTile
                  key={key}
                  label={s.label}
                  value={counts[key]}
                  hint={s.hint}
                  tone={key === "all" ? "default" : STATUS_TONES[key]}
                  active={status === key}
                  onClick={() => setParam("status", key)}
                />
              ))}
            </div>
          )}

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
            <SearchField
              value={query}
              onChange={(value) => setParam("q", value)}
              placeholder="Search truck #, VIN, make, driver, place…"
              className="w-full lg:w-80"
            />
            <BranchSelect value={selectedBranch} onChange={(v) => setParam("branch", v)} branches={branches} />
            <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
              <span className="app-muted text-sm tabular-nums">
                {visible.length} of {inBranch.length}
                {removedView ? " removed" : ""}
              </span>
              {(removed.length > 0 || removedView) && (
                <button
                  type="button"
                  className="app-btn app-btn-sm app-btn-secondary"
                  aria-pressed={removedView}
                  onClick={() => setParam("removed", removedView ? "" : "1")}
                  title={removedView ? "Back to every truck in service" : "Trucks a Fleet Manager removed"}
                >
                  {removedView ? (
                    "All vehicles"
                  ) : (
                    <>
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} /> Removed ({removed.length})
                    </>
                  )}
                </button>
              )}
              {!removedView && (
                <Segmented
                  label="View"
                  value={view}
                  onChange={(v) => setParam("view", v)}
                  options={[
                    { value: "list", label: "List" },
                    { value: "map", label: "Map" },
                  ]}
                />
              )}
            </div>
          </div>

          {view === "map" ? (
            <div className="app-card overflow-hidden rounded-2xl border">
              <FleetMap vehicles={visible} />
              <div className="border-t app-divider px-4 py-3">
                <MapLegend counts={mapCounts} />
              </div>
            </div>
          ) : (
            <div className="app-card overflow-hidden rounded-2xl border">
              {visible.length === 0 ? (
                <EmptyState
                  compact
                  icon={LocalShippingRoundedIcon}
                  title={removedView && !removed.length ? "No trucks have been removed." : "No vehicles match these filters."}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="app-table">
                    <thead>
                      <tr>
                        <th className="!px-4">Vehicle</th>
                        <th>Branch</th>
                        <th>Right now</th>
                        <th>Odometer</th>
                        <th>Last inspection</th>
                        <th>Inspections ({HISTORY_DAYS}d)</th>
                        <th aria-label="Open" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((v) => {
                        const state = gpsState(v.gps);
                        const place = gpsPlace(v.gps);
                        return (
                          <tr
                            key={v.key}
                            tabIndex={0}
                            role="link"
                            aria-label={`Open vehicle ${v.label}`}
                            onClick={() => navigate(`/fleet/vehicles/${v.key}`)}
                            onKeyDown={(e) => e.key === "Enter" && navigate(`/fleet/vehicles/${v.key}`)}
                            className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                          >
                            <td className="!px-4">
                              <Link
                                to={`/fleet/vehicles/${v.key}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold tabular-nums hover:text-[color:var(--app-accent)]"
                              >
                                {v.label}
                              </Link>
                              {v.renamed && (
                                <span className="app-muted block text-xs">{vehicleOrigin(v).hint(v.bistrackLabel)}</span>
                              )}
                              <span className="app-muted block text-xs">
                                {vehicleModel(v.info) || (v.vin ? `VIN ${v.vin}` : "—")}
                              </span>
                              {!v.inService && (
                                <span className="mt-0.5 block" title={v.service?.note || undefined}>
                                  <Pill tone="violet">Out of service</Pill>
                                  <span className="app-muted mt-0.5 block text-xs">{serviceSummary(v.service)}</span>
                                </span>
                              )}
                              {!v.inBistrack && (
                                <span className="mt-0.5 block">
                                  <Pill tone="slate">{v.vehicleId ? "Not in Bistrack" : "GPS only"}</Pill>
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap">
                              {v.branch || <span className="app-muted">—</span>}
                              {v.moved && (
                                <span className="app-muted block text-xs" title="Moved here on the Fleet pages">
                                  Moved{v.bistrackBranch ? ` · ${vehicleOrigin(v).hint(v.bistrackBranch)}` : ""}
                                </span>
                              )}
                            </td>
                            <td className="max-w-[16rem]">
                              <Pill tone={state.tone}>{state.label}</Pill>
                              {(place || v.gps?.driver) && (
                                <span className="app-muted mt-0.5 block truncate text-xs" title={place}>
                                  {[v.gps?.driver, place].filter(Boolean).join(" · ")}
                                </span>
                              )}
                              {v.gps?.ageMinutes !== null && v.gps?.ageMinutes !== undefined && state.key !== "stale" && (
                                <span className="app-muted block text-[11px]">{formatAge(v.gps.ageMinutes)} ago</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap tabular-nums">{formatMiles(odometerOf(v))}</td>
                            <td className="whitespace-nowrap">
                              {v.stats.lastAt ? (
                                <span className="flex flex-col gap-0.5">
                                  <span className="flex items-center gap-1.5">
                                    <span className="font-medium">{relativeDay(v.stats.lastAt)}</span>
                                    <ResultChip result={v.stats.lastResult} />
                                  </span>
                                  <span className="app-muted text-xs tabular-nums">
                                    {formatDateTime(v.stats.lastAt)}
                                    {v.stats.lastInspector ? ` · ${v.stats.lastInspector}` : ""}
                                  </span>
                                </span>
                              ) : (
                                <span className="app-muted">None in {HISTORY_DAYS} days</span>
                              )}
                            </td>
                            <td className="tabular-nums">
                              {v.stats.count}
                              {v.stats.medianSeconds !== null && v.stats.medianSeconds !== undefined && (
                                <span
                                  className={`ml-1.5 text-xs ${v.stats.medianSeconds < 30 ? "text-amber-700 dark:text-amber-300" : "app-muted"}`}
                                  title="Typical (median) inspection time"
                                >
                                  ~{formatDuration(v.stats.medianSeconds)} each
                                </span>
                              )}
                              {v.stats.flagged > 0 && (
                                <Pill tone="amber" className="ml-2">
                                  {v.stats.flagged} flagged
                                </Pill>
                              )}
                              {v.stats.missed > 0 && (
                                <Pill tone="rose" className="ml-2">
                                  {v.stats.missed} drove w/o
                                </Pill>
                              )}
                            </td>
                            <td className="w-8 text-right">
                              <ChevronRightRoundedIcon className="app-muted" fontSize="small" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </PageShell>
  );
};

export default VehicleList;
